import { useEffect, useRef, useState } from 'react';
import type Hls from 'hls.js';
import { Headphones, VolumeX } from 'lucide-react';
import { Panel } from './Panel';
import { Notice } from './Status';

/**
 * Confidence player. Plays the HLS source directly in the browser (hls.js), so it
 * is independent of the server-side measurement; if the browser cannot load it
 * (commonly a CORS restriction on the CDN), monitoring is not affected.
 * Audio is muted until the operator explicitly turns on headphone monitoring.
 */
export type PlayerSource = { kind: 'hls'; url: string; serverPreview?: boolean } | { kind: 'youtube'; videoId: string } | { kind: 'none'; reason: string };

export function Player({ source }: { source: PlayerSource }) {
  if (source.kind === 'youtube') {
    return (
      <Panel title="Confidence monitor">
        <iframe
          className="player"
          src={`https://www.youtube-nocookie.com/embed/${encodeURIComponent(source.videoId)}?autoplay=1&mute=1&playsinline=1`}
          title="YouTube player"
          allow="autoplay; encrypted-media; picture-in-picture"
          referrerPolicy="strict-origin-when-cross-origin"
        />
        <p className="muted small">YouTube's own player, for viewing only. Use its volume control to listen. All measurements are made on the server.</p>
      </Panel>
    );
  }
  if (source.kind === 'none') {
    return (
      <Panel title="Confidence monitor">
        <div className="player player-empty">{source.reason}</div>
      </Panel>
    );
  }
  return <HlsPlayer url={source.url} serverPreview={!!source.serverPreview} />;
}

function HlsPlayer({ url, serverPreview }: { url: string; serverPreview: boolean }) {
  const video = useRef<HTMLVideoElement | null>(null);
  const [muted, setMuted] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const el = video.current;
    if (!el) return;
    setError(null);
    let hls: Hls | null = null;
    let cancelled = false;
    // hls.js is loaded on demand to keep the main bundle small.
    void import('hls.js').then(({ default: HlsLib }) => {
      if (cancelled) return;
      if (HlsLib.isSupported()) {
        hls = new HlsLib({ lowLatencyMode: !serverPreview, liveSyncDurationCount: 3, manifestLoadingMaxRetry: 20, manifestLoadingRetryDelay: 2000 });
        hls.on(HlsLib.Events.ERROR, (_e, data) => {
          if (!data.fatal) return;
          setError(
            data.type === HlsLib.ErrorTypes.NETWORK_ERROR
              ? serverPreview
                ? 'The server preview is not available yet. It starts a few seconds after the SRT feed connects.'
                : 'The preview could not load the stream in this browser, often because the CDN does not allow cross-origin playback. Server-side monitoring is not affected.'
              : 'The preview stopped because of a playback error. Server-side monitoring is not affected.',
          );
        });
        hls.loadSource(url);
        hls.attachMedia(el);
      } else if (el.canPlayType('application/vnd.apple.mpegurl')) {
        el.src = url;
      } else {
        setError('This browser cannot play HLS.');
      }
      void el.play().catch(() => {});
    });
    return () => {
      cancelled = true;
      hls?.destroy();
      el.removeAttribute('src');
      el.load();
    };
  }, [url, serverPreview]);

  useEffect(() => {
    if (video.current) video.current.muted = muted;
  }, [muted]);

  return (
    <Panel
      title="Confidence monitor"
      actions={
        <button
          type="button"
          className={`btn btn-sm ${muted ? '' : 'btn-primary'}`}
          aria-pressed={!muted}
          onClick={() => setMuted((m) => !m)}
        >
          {muted ? <VolumeX size={14} aria-hidden /> : <Headphones size={14} aria-hidden />}
          {muted ? 'Audio muted' : 'Listening'}
        </button>
      }
    >
      <video ref={video} className="player" muted playsInline autoPlay aria-label="Stream preview" />
      {error && <Notice level="warn">{error}</Notice>}
      <p className="muted small">
        {serverPreview ? 'Low-bitrate preview transcoded by the server from the SRT feed.' : 'Browser preview, for confidence only.'} Turn on audio to listen on headphones. All measurements are made on the server.
      </p>
    </Panel>
  );
}
